import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, BookOpen, Zap, Brain, PlayCircle, Sparkles,
  Check, Loader2, ChevronDown, ChevronUp, MessageCircle, FileText, Film, Lock, Crown
} from 'lucide-react';
import { hasFeatureAccess, hasSubjectAccess, ALL_SUBJECTS, getQuizLimitInfo } from '../utils/subscriptionFeatures';
import { LockedFeatureButton, LockBadge, SubjectLockBadge } from '../components/LockedFeature';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function ChaptersPage({ params, onNavigate }) {
  const { api, user } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(params?.subject || 'Math');
  const [schoolProgress, setSchoolProgress] = useState(null);
  const [studentProgress, setStudentProgress] = useState([]);
  const [essence, setEssence] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [generatingEssence, setGeneratingEssence] = useState(null); // chapter number or null
  const [generatingQuiz, setGeneratingQuiz] = useState(null); // chapter number or null
  const [expandedCard, setExpandedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizzesTakenToday, setQuizzesTakenToday] = useState(0);

  const className = user?.class_name || 'Class 8';
  const subscription = user?.subscription || 'free';
  const subjects = ALL_SUBJECTS;
  
  // Check feature access
  const canAccessChat = hasFeatureAccess(subscription, 'ai_chat');
  const canAccessVideo = hasFeatureAccess(subscription, 'ai_video_explainer');
  const quizLimitInfo = getQuizLimitInfo(subscription, quizzesTakenToday);

  const fetchData = useCallback(async () => {
    try {
      const [chapRes, progRes, dashRes] = await Promise.all([
        api.get(`/chapters?subject=${selectedSubject}&class_name=${className}`),
        api.get(`/school/progress?class_name=${className}`),
        api.get('/student/dashboard')
      ]);
      // Sort chapters by number (NCERT curriculum order)
      const sortedChapters = (chapRes.data || []).sort((a, b) => a.number - b.number);
      setChapters(sortedChapters);
      const sp = progRes.data.find(p => p.subject === selectedSubject);
      setSchoolProgress(sp);
      setStudentProgress(dashRes.data.student_progress || []);
      setQuizzesTakenToday(dashRes.data.quizzes_today || 0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [api, selectedSubject, className]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  const generateEssence = async (chapter) => {
    setSelectedChapter(chapter);
    setGeneratingEssence(chapter.number);
    try {
      const res = await api.post('/ai/generate-essence', {
        subject: selectedSubject,
        chapter_number: chapter.number,
        class_name: className,
      });
      setEssence(res.data);
    } catch (err) {
      console.error(err);
    }
    setGeneratingEssence(null);
  };

  const startQuiz = async (chapter) => {
    setGeneratingQuiz(chapter.number);
    try {
      const res = await api.post('/ai/generate-quiz', {
        subject: selectedSubject,
        chapter_number: chapter.number,
        class_name: className,
        num_questions: 5,
      });
      onNavigate('quiz', { quest: res.data });
    } catch (err) {
      console.error(err);
    }
    setGeneratingQuiz(null);
  };

  const getMastery = (chapterNum) => {
    const p = studentProgress.find(
      s => s.subject === selectedSubject && s.chapter_number === chapterNum
    );
    return p?.mastery || 0;
  };

  const isCompleted = (chapterNum) => {
    return schoolProgress?.completed_chapters?.includes(chapterNum);
  };

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button data-testid="back-to-dashboard" onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="chapters-title">
            {subjectIcons[selectedSubject]} {selectedSubject}
          </h1>
          <p className="text-xs text-slate-500">{className} - CBSE</p>
        </div>
        {/* Quiz limit indicator for Basic plan */}
        {!quizLimitInfo.unlimited && (
          <div className="ml-auto flex items-center gap-1.5 bg-slate-800/50 rounded-lg px-2 py-1">
            <Brain className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs text-slate-400">
              {quizLimitInfo.remaining}/{quizLimitInfo.limit} quizzes left
            </span>
          </div>
        )}
      </div>

      {/* Subject Tabs with Lock indicators for Basic plan */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
        {subjects.map((s, index) => {
          const isLocked = !hasSubjectAccess(subscription, index, user?.selected_subjects);
          return (
            <button
              key={s}
              data-testid={`tab-${s.toLowerCase().replace(/ /g, '-')}`}
              onClick={() => {
                if (isLocked) {
                  onNavigate('subscription');
                } else {
                  setSelectedSubject(s); setEssence(null); setSelectedChapter(null);
                }
              }}
              className={`relative px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                s === selectedSubject
                  ? 'bg-violet-600 text-white glow-violet'
                  : isLocked 
                    ? 'bg-slate-900/30 text-slate-600 border border-slate-800/50'
                    : 'bg-slate-900/50 text-slate-400 border border-slate-800 hover:border-slate-700'
              }`}
            >
              {subjectIcons[s]} {s}
              {isLocked && (
                <Lock className="absolute -top-1 -right-1 w-3.5 h-3.5 text-amber-400 bg-slate-900 rounded-full p-0.5" />
              )}
            </button>
          );
        })}
      </div>

      {/* Upgrade banner for Basic users */}
      {subscription === 'basic' && (
        <SubjectLockBadge onUpgrade={() => onNavigate('subscription')} />
      )}

      {/* Progress Bar */}
      {schoolProgress && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-4" data-testid="school-progress-bar">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500 uppercase tracking-widest">School Progress</span>
            <span className="text-xs text-emerald-400">
              {schoolProgress.completed_chapters?.length || 0}/5 chapters
            </span>
          </div>
          <div className="flex gap-1.5">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className={`h-3 w-full rounded-full ${
                  isCompleted(i + 1) ? 'bg-emerald-500' :
                  schoolProgress.next_chapter === i + 1 ? 'bg-amber-500 animate-pulse' :
                  'bg-slate-800'
                }`} />
                <span className="text-[10px] text-slate-600">Ch{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Chapters List */}
          <div className="space-y-3 mb-5">
            {chapters.map(ch => {
              const mastery = getMastery(ch.number);
              const completed = isCompleted(ch.number);
              return (
                <motion.div
                  key={ch.number}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: ch.number * 0.05 }}
                  data-testid={`chapter-card-${ch.number}`}
                  className={`bg-slate-900/50 border rounded-2xl overflow-hidden transition-all ${
                    completed ? 'border-emerald-500/20' : 'border-slate-800'
                  } hover:border-violet-500/30`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {completed && <Check className="w-4 h-4 text-emerald-400" />}
                          <span className="text-xs text-slate-500">Chapter {ch.number}</span>
                        </div>
                        <h3 className="text-sm font-semibold text-white">Ch {ch.number}: {ch.name}</h3>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {ch.topics.map((t, i) => (
                            <span key={i} className="text-[10px] bg-slate-800/80 text-slate-400 px-2 py-0.5 rounded-full">{t}</span>
                          ))}
                        </div>
                      </div>
                      <div className="w-12 h-12 rounded-full border-2 flex items-center justify-center ml-3 shrink-0" style={{
                        borderColor: mastery >= 80 ? '#10b981' : mastery >= 50 ? '#f59e0b' : '#334155'
                      }}>
                        <span className="text-xs font-bold" style={{
                          color: mastery >= 80 ? '#10b981' : mastery >= 50 ? '#f59e0b' : '#64748b'
                        }}>{mastery}%</span>
                      </div>
                    </div>

                    {/* Action Buttons - 5 per chapter with feature locks */}
                    <div className="grid grid-cols-5 gap-1.5 mt-3">
                      {/* AI Chat - Pro only */}
                      <button
                        data-testid={`chat-btn-${ch.number}`}
                        onClick={() => canAccessChat ? onNavigate('chat', { subject: selectedSubject, chapter: ch.number }) : onNavigate('subscription')}
                        className={`relative bg-blue-600/10 border border-blue-500/20 text-blue-300 rounded-lg py-2 text-[10px] font-medium hover:bg-blue-600/20 transition-all flex flex-col items-center gap-0.5 ${!canAccessChat ? 'opacity-60' : ''}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        Chat
                        {!canAccessChat && <Lock className="absolute -top-1 -right-1 w-3 h-3 text-amber-400" />}
                      </button>
                      
                      {/* Chapter Essence - Basic+ */}
                      <button
                        data-testid={`essence-btn-${ch.number}`}
                        onClick={() => generateEssence(ch)}
                        disabled={generatingEssence === ch.number}
                        className="bg-violet-600/10 border border-violet-500/20 text-violet-300 rounded-lg py-2 text-[10px] font-medium hover:bg-violet-600/20 transition-all flex flex-col items-center gap-0.5 disabled:opacity-50"
                      >
                        {generatingEssence === ch.number ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        Essence
                      </button>
                      
                      {/* Quiz - Basic+ but with daily limit for Basic */}
                      <button
                        data-testid={`quiz-btn-${ch.number}`}
                        onClick={() => {
                          if (!quizLimitInfo.unlimited && quizLimitInfo.remaining <= 0) {
                            onNavigate('subscription');
                          } else {
                            startQuiz(ch);
                          }
                        }}
                        disabled={generatingQuiz === ch.number}
                        className={`relative bg-amber-600/10 border border-amber-500/20 text-amber-300 rounded-lg py-2 text-[10px] font-medium hover:bg-amber-600/20 transition-all flex flex-col items-center gap-0.5 disabled:opacity-50 ${!quizLimitInfo.unlimited && quizLimitInfo.remaining <= 0 ? 'opacity-60' : ''}`}
                      >
                        {generatingQuiz === ch.number ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
                        Quiz
                        {!quizLimitInfo.unlimited && quizLimitInfo.remaining <= 0 && <Lock className="absolute -top-1 -right-1 w-3 h-3 text-amber-400" />}
                      </button>
                      
                      {/* YouTube Videos - Basic+ */}
                      <button
                        data-testid={`video-btn-${ch.number}`}
                        onClick={() => onNavigate('video', { subject: selectedSubject, chapter: ch.number })}
                        className="bg-cyan-600/10 border border-cyan-500/20 text-cyan-300 rounded-lg py-2 text-[10px] font-medium hover:bg-cyan-600/20 transition-all flex flex-col items-center gap-0.5"
                      >
                        <PlayCircle className="w-3.5 h-3.5" />
                        YT
                      </button>
                      
                      {/* AI Video Explainer - Pro only */}
                      <button
                        data-testid={`explainer-btn-${ch.number}`}
                        onClick={() => canAccessVideo ? onNavigate('explainer-video', { subject: selectedSubject, chapter: ch.number }) : onNavigate('subscription')}
                        className={`relative bg-emerald-600/10 border border-emerald-500/20 text-emerald-300 rounded-lg py-2 text-[10px] font-medium hover:bg-emerald-600/20 transition-all flex flex-col items-center gap-0.5 ${!canAccessVideo ? 'opacity-60' : ''}`}
                      >
                        <Film className="w-3.5 h-3.5" />
                        Video
                        {!canAccessVideo && <Lock className="absolute -top-1 -right-1 w-3 h-3 text-amber-400" />}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Essence Modal */}
          <AnimatePresence>
            {essence && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                onClick={() => setEssence(null)}
              >
                <motion.div
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 100, opacity: 0 }}
                  onClick={e => e.stopPropagation()}
                  className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto"
                  data-testid="essence-modal"
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold text-white font-['Outfit']">
                        {essence.essence?.title || essence.chapter_name}
                      </h2>
                      <button onClick={() => setEssence(null)} className="text-slate-500 hover:text-white text-sm">Close</button>
                    </div>

                    {/* Summary */}
                    {essence.essence?.summary && (
                      <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3 mb-4">
                        <p className="text-sm text-violet-200">{essence.essence.summary}</p>
                      </div>
                    )}

                    {/* Key Concepts */}
                    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Key Concepts</h3>
                    <div className="space-y-2 mb-5">
                      {essence.essence?.key_concepts?.map((c, i) => (
                        <div key={i} className="bg-slate-800/50 rounded-xl overflow-hidden" data-testid={`concept-${i}`}>
                          <button
                            onClick={() => setExpandedCard(expandedCard === `concept-${i}` ? null : `concept-${i}`)}
                            className="w-full flex items-center justify-between p-3 text-left"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-lg bg-violet-600/20 flex items-center justify-center text-xs text-violet-400 font-bold">{i + 1}</div>
                              <span className="text-sm text-white font-medium">{c.title}</span>
                            </div>
                            {expandedCard === `concept-${i}` ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                          </button>
                          <AnimatePresence>
                            {expandedCard === `concept-${i}` && (
                              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                <div className="px-3 pb-3 border-t border-slate-800 pt-2">
                                  <p className="text-xs text-slate-300 mb-1">{c.explanation}</p>
                                  {c.example && <p className="text-xs text-cyan-400 mt-1">Example: {c.example}</p>}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>

                    {/* Formulas */}
                    {essence.essence?.formulas?.length > 0 && (
                      <>
                        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Key Formulas</h3>
                        <div className="space-y-2 mb-5">
                          {essence.essence.formulas.map((f, i) => (
                            <div key={i} className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3" data-testid={`formula-${i}`}>
                              <p className="text-sm text-amber-200 font-mono">{f.formula}</p>
                              <p className="text-xs text-slate-400 mt-1">{f.description}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Practice Questions */}
                    {essence.essence?.practice_questions?.length > 0 && (
                      <>
                        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Practice Questions</h3>
                        <div className="space-y-2">
                          {essence.essence.practice_questions.map((q, i) => (
                            <div key={i} className="bg-slate-800/50 rounded-xl overflow-hidden" data-testid={`practice-q-${i}`}>
                              <button
                                onClick={() => setExpandedCard(expandedCard === `pq-${i}` ? null : `pq-${i}`)}
                                className="w-full flex items-center justify-between p-3 text-left"
                              >
                                <span className="text-sm text-white">Q{i + 1}: {q.question}</span>
                                {expandedCard === `pq-${i}` ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                              </button>
                              <AnimatePresence>
                                {expandedCard === `pq-${i}` && (
                                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                    <div className="px-3 pb-3 border-t border-slate-800 pt-2">
                                      <p className="text-xs text-emerald-400">Answer: {q.answer}</p>
                                      {q.hint && <p className="text-xs text-slate-500 mt-1">Hint: {q.hint}</p>}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
